import { useInView } from '@/hooks/useInView';
import { Check, Lock } from 'lucide-react';

const packages = [
  {
    name: 'Basis-Präsenz',
    subtitle: 'Der perfekte Einstieg',
    features: [
      'Website dauerhaft online',
      'Eigene Wunsch-Domain',
      'Sorglos-Hosting & SSL',
      '1 Seite (One-Pager)',
      '2 Änderungen / Monat',
      'Kleine Anpassungen (z.B. Telefonnummer, Textfehler)',
      'Änderungen via WhatsApp melden',
    ],
    prices: { setup: '199,00 €', monthly: '40,00 €/mtl.', twelve: '34,99 €/mtl.', twentyfour: '29,99 €/mtl.' },
    popular: false,
    blurPrices: false,
  },
  {
    name: 'Business-Upgrade',
    subtitle: 'Für wachsende Unternehmen',
    features: [
      'Website dauerhaft online',
      'Eigene Wunsch-Domain',
      'Sorglos-Hosting & SSL',
      'Bis zu 5 Seiten',
      'Bis zu 10 Änderungen / Monat',
      'Content-Pflege (z.B. News-Beiträge)',
      'Änderungen via WhatsApp melden',
    ],
    prices: { setup: '349,00 €', monthly: '60,00 €/mtl.', twelve: '54,99 €/mtl.', twentyfour: '49,99 €/mtl.' },
    popular: true,
    blurPrices: false,
  },
  {
    name: 'Premium-Partnerschaft',
    subtitle: 'Die Komplettlösung',
    features: [
      'Website dauerhaft online',
      'Eigene Wunsch-Domain',
      'Sorglos-Hosting & SSL',
      'Seitenanzahl nach Vereinbarung',
      'Flatrate Inhaltspflege',
      'Full-Design-Service (inkl. Layout-Anpassungen)',
      'Änderungen via WhatsApp melden',
    ],
    prices: { setup: '600,00 €', monthly: '100,00 €/mtl.', twelve: '89,99 €/mtl.', twentyfour: '79,99 €/mtl.' },
    popular: false,
    blurPrices: true,
  },
];

export default function PaketeSection() {
  const [ref, inView] = useInView();

  const scrollToKontakt = () => {
    document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="pakete" ref={ref} data-testid="pakete-section" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className={`text-center mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-600 font-['Outfit']">Pakete</span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-light tracking-tight text-slate-900 font-['Outfit']">
            Ihr Paket. Ihre <span className="gradient-text font-normal">Zukunft.</span>
          </h2>
          <p className="mt-4 text-base text-slate-500 max-w-xl mx-auto">
            Transparent, fair und ohne versteckte Kosten.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {packages.map((pkg, i) => (
            <div
              key={pkg.name}
              className={`relative bg-white/60 backdrop-blur-2xl border rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 ${
                pkg.popular
                  ? 'border-purple-300/50 shadow-[0_20px_40px_rgba(168,85,247,0.12)] scale-[1.02] lg:scale-105'
                  : 'border-white/80 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(236,72,153,0.1)]'
              } ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 120}ms` }}
              data-testid={`package-card-${i}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1.5 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white text-xs font-semibold tracking-wider shadow-lg shadow-purple-500/30">
                  BELIEBT
                </div>
              )}

              <h3 className="text-2xl font-semibold text-slate-900 font-['Outfit']">{pkg.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{pkg.subtitle}</p>

              {/* Blurred Prices */}
              <div className="mt-6 p-5 rounded-2xl bg-slate-50/80 border border-slate-100/80" data-testid={`package-prices-${i}`}>
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Einrichtung</span>
                    <span className={`text-sm font-medium text-slate-700 whitespace-nowrap ${pkg.blurPrices ? 'price-blur' : ''}`}>{pkg.prices.setup}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">Monatlich kündbar</span>
                    <span className={`text-sm font-medium text-slate-700 whitespace-nowrap ${pkg.blurPrices ? 'price-blur' : ''}`}>{pkg.prices.monthly}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">12-Monats-Vertrag</span>
                    <span className={`text-sm font-medium text-slate-700 whitespace-nowrap ${pkg.blurPrices ? 'price-blur' : ''}`}>{pkg.prices.twelve}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-400">24-Monats-Vertrag</span>
                    <span className={`text-sm font-medium text-slate-700 whitespace-nowrap ${pkg.blurPrices ? 'price-blur' : ''}`}>{pkg.prices.twentyfour}</span>
                  </div>
                </div>
                {pkg.blurPrices && (
                  <div className="mt-4 flex items-center gap-2 text-xs text-purple-600">
                    <Lock size={12} />
                    <span className="font-medium">Preise auf Anfrage für maßgeschneiderte Visionen</span>
                  </div>
                )}
              </div>

              {/* Features */}
              <ul className="mt-6 space-y-3">
                {pkg.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500/10 to-pink-500/10 flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-purple-600" />
                    </div>
                    <span className="text-sm text-slate-600">{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={scrollToKontakt}
                className={`mt-8 w-full py-3.5 rounded-full font-medium text-sm transition-all active:scale-95 ${
                  pkg.popular
                    ? 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40'
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                }`}
                data-testid={`package-cta-${i}`}
              >
                Anfrage stellen
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
